import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { collection, doc, setDoc, serverTimestamp, getDocs, getDoc } from "firebase/firestore";
import { db } from "../../../Firebase";
import { V3Event, V3Problem } from "../types/v3types";
import { uploadMultipleImages } from "../utils/storage";
import { useAuth } from "../hooks/useAuth";
import styled from "styled-components";

const Container = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
  padding: 20px;
`;

const Header = styled.div`
  max-width: 1200px;
  margin: 0 auto 30px;
`;

const Title = styled.h1`
  font-size: 28px;
  margin-bottom: 10px;
`;

const Form = styled.form`
  max-width: 1200px;
  margin: 0 auto;
  background: white;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Section = styled.div`
  margin-bottom: 30px;
  padding-bottom: 30px;
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  margin-bottom: 20px;
  color: #333;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  color: #555;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
`;

const ProblemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ProblemCard = styled.div`
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 20px;
  background: #fafafa;
`;

const ProblemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const ProblemTitle = styled.h3`
  font-size: 18px;
  margin: 0;
`;

const RemoveButton = styled.button`
  padding: 6px 12px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background: #c82333;
  }
`;

const ImageUploadArea = styled.div`
  border: 2px dashed #ddd;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s;

  &:hover {
    border-color: #667eea;
  }
`;

const ImagePreview = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 10px;
`;

const PreviewImage = styled.img`
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 4px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 30px;
`;

const Button = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const SaveButton = styled(Button)`
  background: #667eea;
  color: white;

  &:hover:not(:disabled) {
    background: #5a67d8;
  }
`;

const CancelButton = styled(Button)`
  background: #e0e0e0;
  color: #333;

  &:hover {
    background: #d0d0d0;
  }
`;

const AddProblemButton = styled(Button)`
  background: #28a745;
  color: white;

  &:hover {
    background: #218838;
  }
`;

const ErrorMessage = styled.div`
  background: #ffe3e3;
  color: #c92a2a;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 20px;
`;

const V3EventCreate: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user: firebaseUser } = useAuth();
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  const [eventData, setEventData] = useState({
    eventName: "",
    description: "",
    isPublic: true,
    tags: "",
    maxParticipants: "",
    timeLimit: "",
    allowLateJoin: true,
    showRankingDuringEvent: true,
    scoringMode: "dynamic" as "dynamic" | "normal" | "custom",
    dynamicScoreMultiplier: "1.2",
    useExistingSet: false,
    selectedSetId: "",
  });
  
  const [availableSets, setAvailableSets] = useState<Array<{
    setId: string;
    setName: string;
    creator: string;
    problemCount: number;
  }>>([]);
  
  const [problems, setProblems] = useState<Array<{
    id: string;
    title: string;
    beforeImageFiles: File[];
    beforeImageUrls: string[];
    imageFiles: File[];
    imageUrls: string[];
    answerImageFiles: File[];
    answerImageUrls: string[];
    answers: string;
    choices: string;
    baseScore: string;
    timeLimit: string;
    maxSubmissions: string;
    inputType: string;
    hint: string;
    explanation: string;
    imageSwitchMode: "manual" | "auto";
    autoSwitchInterval: string;
  }>>([{
    id: "1",
    title: "",
    beforeImageFiles: [],
    beforeImageUrls: [],
    imageFiles: [],
    imageUrls: [],
    answerImageFiles: [],
    answerImageUrls: [],
    answers: "",
    choices: "",
    baseScore: "100",
    timeLimit: "300",
    maxSubmissions: "1",
    inputType: "text",
    hint: "",
    explanation: "",
    imageSwitchMode: "manual",
    autoSwitchInterval: "5",
  }]);

  useEffect(() => {
    // 利用可能なセットを取得
    const fetchAvailableSets = async () => {
      try {
        const setsSnapshot = await getDocs(collection(db, "v3sets"));
        const sets = setsSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            setId: doc.id,
            setName: data.setName || doc.id,
            creator: data.creator || "unknown",
            problemCount: data.problemCount || 0,
          };
        });
        setAvailableSets(sets);
      } catch (error) {
        console.error("Error fetching sets:", error);
      }
    };
    
    fetchAvailableSets();
  }, []);

  const handleEventChange = (field: string, value: any) => {
    setEventData(prev => ({ ...prev, [field]: value }));
    
    // 既存セット使用モードを切り替えた場合、問題をクリア
    if (field === "useExistingSet") {
      if (value) {
        setProblems([]);
      } else {
        // デフォルトの問題を追加
        setProblems([{
          id: "1",
          title: "",
          beforeImageFiles: [],
          beforeImageUrls: [],
          imageFiles: [],
          imageUrls: [],
          answerImageFiles: [],
          answerImageUrls: [],
          answers: "",
          choices: "",
          baseScore: "100",
          timeLimit: "300",
          maxSubmissions: "1",
          inputType: "text",
          hint: "",
          explanation: "",
          imageSwitchMode: "manual",
          autoSwitchInterval: "5",
        }]);
      }
    }
  };

  const handleProblemChange = (problemId: string, field: string, value: any) => {
    setProblems(prev => prev.map(p => 
      p.id === problemId ? { ...p, [field]: value } : p
    ));
  };

  const handleImageSelect = (problemId: string, type: "beforeImage" | "image" | "answerImage", files: FileList | null) => {
    if (!files) return;
    
    const fileArray = Array.from(files);
    const field = type === "beforeImage" ? "beforeImageFiles" : type === "image" ? "imageFiles" : "answerImageFiles";
    
    handleProblemChange(problemId, field, fileArray);
    
    // Create preview URLs
    const urls = fileArray.map(file => URL.createObjectURL(file));
    handleProblemChange(problemId, field.replace("Files", "Urls"), urls);
  };

  const addProblem = () => {
    const newId = String(problems.length + 1);
    setProblems(prev => [...prev, {
      id: newId,
      title: "",
      beforeImageFiles: [],
      beforeImageUrls: [],
      imageFiles: [],
      imageUrls: [],
      answerImageFiles: [],
      answerImageUrls: [],
      answers: "",
      choices: "",
      baseScore: "100",
      timeLimit: "300",
      maxSubmissions: "1",
      inputType: "text",
      hint: "",
      explanation: "",
      imageSwitchMode: "manual",
      autoSwitchInterval: "5",
    }]);
  };

  const removeProblem = (problemId: string) => {
    setProblems(prev => prev.filter(p => p.id !== problemId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (!firebaseUser) {
        console.error('No authenticated user found');
        navigate(`/v3/${projectId}/start`);
        return;
      }
      
      console.log('Authenticated user:', firebaseUser.uid);

      // Validate required fields
      if (!eventData.eventName.trim()) {
        setError("イベント名を入力してください");
        setLoading(false);
        return;
      }

      // Validate problems
      for (let i = 0; i < problems.length; i++) {
        const problem = problems[i];
        if (problem.imageFiles.length === 0) {
          setError(`問題 ${i + 1} に画像を追加してください`);
          setLoading(false);
          return;
        }
        if (!problem.answers.trim()) {
          setError(`問題 ${i + 1} の正解を入力してください`);
          setLoading(false);
          return;
        }
      }

      const userId = firebaseUser.uid + "_" + projectId;
      const eventId = `event_${Date.now()}`;

      // Upload images and create problems
      const uploadedProblems: V3Problem[] = await Promise.all(
        problems.map(async (problem, index) => {
          let beforeImageUrls: string[] = [];
          let imageUrls: string[] = [];
          let answerImageUrls: string[] = [];

          if (problem.beforeImageFiles.length > 0) {
            beforeImageUrls = await uploadMultipleImages(
              problem.beforeImageFiles,
              projectId!,
              eventId,
              problem.id,
              "before"
            );
          }

          if (problem.imageFiles.length > 0) {
            imageUrls = await uploadMultipleImages(
              problem.imageFiles,
              projectId!,
              eventId,
              problem.id,
              "problem"
            );
          }

          if (problem.answerImageFiles.length > 0) {
            answerImageUrls = await uploadMultipleImages(
              problem.answerImageFiles,
              projectId!,
              eventId,
              problem.id,
              "answer"
            );
          }

          const problemData: V3Problem = {
            problemId: problem.id,
            title: problem.title || `問題 ${index + 1}`,
            imageUrls,
            answers: problem.answers.split(",").map(a => a.trim()).filter(a => a),
            baseScore: Number(problem.baseScore) || 100,
            timeLimit: Number(problem.timeLimit) || 300,
            maxSubmissions: Number(problem.maxSubmissions) || 3,
            inputType: problem.inputType as any,
            order: index,
          };

          // Add optional fields only if they have values
          if (beforeImageUrls.length > 0) {
            problemData.beforeImageUrls = beforeImageUrls;
          }
          if (answerImageUrls.length > 0) {
            problemData.answerImageUrls = answerImageUrls;
          }
          if (problem.choices && problem.choices.trim()) {
            problemData.choices = problem.choices.split(",").map(c => c.trim()).filter(c => c);
          }
          if (problem.hint && problem.hint.trim()) {
            problemData.hint = problem.hint.trim();
          }
          if (problem.explanation && problem.explanation.trim()) {
            problemData.explanation = problem.explanation.trim();
          }
          if (imageUrls.length > 1 || beforeImageUrls.length > 0) {
            problemData.imageSettings = {
              switchMode: problem.imageSwitchMode,
            };
            if (problem.imageSwitchMode === "auto") {
              problemData.imageSettings.autoSwitchInterval = Number(problem.autoSwitchInterval) || 5;
            }
          }

          return problemData;
        })
      );

      // Calculate base scores based on scoring mode
      if (eventData.scoringMode === "normal") {
        // For normal mode, all problems get 100 points
        uploadedProblems.forEach(problem => {
          problem.baseScore = 100;
        });
      } else if (eventData.scoringMode === "dynamic") {
        // For dynamic mode, apply multiplier to each subsequent problem
        const multiplier = Number(eventData.dynamicScoreMultiplier) || 1.2;
        uploadedProblems.forEach((problem, index) => {
          problem.baseScore = Math.round(100 * Math.pow(multiplier, index));
        });
      }
      // For custom mode, keep the scores as entered

      // Build event document with required fields
      const eventDoc: any = {
        eventId,
        projectId: projectId!,
        eventName: eventData.eventName.trim(),
        creatorId: userId,
        isActive: true,
        isPublic: eventData.isPublic,
        problems: uploadedProblems,
        scoringMode: eventData.scoringMode,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        useExistingSet: eventData.useExistingSet,
        selectedSetId: eventData.useExistingSet ? eventData.selectedSetId : null,
      };
      
      // Add dynamic score multiplier if in dynamic mode
      if (eventData.scoringMode === "dynamic") {
        eventDoc.dynamicScoreMultiplier = Number(eventData.dynamicScoreMultiplier) || 1.2;
      }

      // Build settings object
      const settings: any = {
        allowLateJoin: eventData.allowLateJoin,
        showRankingDuringEvent: eventData.showRankingDuringEvent,
      };

      // Add optional settings
      if (eventData.maxParticipants && eventData.maxParticipants.trim()) {
        settings.maxParticipants = Number(eventData.maxParticipants);
      }
      if (eventData.timeLimit && eventData.timeLimit.trim()) {
        settings.timeLimit = Number(eventData.timeLimit);
      }

      // Only add settings if it has properties
      if (Object.keys(settings).length > 0) {
        eventDoc.settings = settings;
      }

      // Add optional fields only if they have values
      if (eventData.description && eventData.description.trim()) {
        eventDoc.description = eventData.description.trim();
      }
      if (eventData.tags && eventData.tags.trim()) {
        eventDoc.tags = eventData.tags.split(",").map(t => t.trim()).filter(t => t);
      }

      await setDoc(doc(db, "v3events", eventId), eventDoc);

      navigate(`/v3/${projectId}/mypage`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "イベントの作成に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Header>
        <Title>新規イベント作成</Title>
        <div style={{ 
          background: 'rgba(220, 20, 60, 0.1)', 
          border: '1px solid #dc143c',
          borderRadius: '8px',
          padding: '15px',
          marginTop: '20px',
          fontSize: '14px',
          lineHeight: '1.6'
        }}>
          <strong>📌 推奨設定</strong>
          <ul style={{ margin: '10px 0 0 20px', paddingLeft: '0' }}>
            <li>問題数: 10問（15問だと多い）</li>
            <li>解答回数: 1回</li>
            <li>判定: 入力直後（デフォルト）</li>
            <li>得点設定: ダイナミック（逆転しやすい）</li>
          </ul>
        </div>
      </Header>

      <Form onSubmit={handleSubmit}>
        {error && <ErrorMessage>{error}</ErrorMessage>}

        <Section>
          <SectionTitle>イベント基本情報</SectionTitle>
          
          <FormGroup>
            <Label htmlFor="eventName">イベント名 *</Label>
            <Input
              id="eventName"
              type="text"
              value={eventData.eventName}
              onChange={(e) => handleEventChange("eventName", e.target.value)}
              required
              disabled={loading}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="description">説明</Label>
            <TextArea
              id="description"
              value={eventData.description}
              onChange={(e) => handleEventChange("description", e.target.value)}
              disabled={loading}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="tags">タグ（カンマ区切り）</Label>
            <Input
              id="tags"
              type="text"
              value={eventData.tags}
              onChange={(e) => handleEventChange("tags", e.target.value)}
              placeholder="例: 初級, パズル, 論理"
              disabled={loading}
            />
          </FormGroup>

          <FormGroup>
            <CheckboxGroup>
              <Checkbox
                id="isPublic"
                type="checkbox"
                checked={eventData.isPublic}
                onChange={(e) => handleEventChange("isPublic", e.target.checked)}
                disabled={loading}
              />
              <Label htmlFor="isPublic">公開イベントにする</Label>
            </CheckboxGroup>
          </FormGroup>
        </Section>

        <Section>
          <SectionTitle>得点設定</SectionTitle>
          
          <FormGroup>
            <Label htmlFor="scoringMode">得点モード</Label>
            <Select
              id="scoringMode"
              value={eventData.scoringMode}
              onChange={(e) => handleEventChange("scoringMode", e.target.value)}
              disabled={loading}
            >
              <option value="dynamic">ダイナミック（倍率制）</option>
              <option value="normal">通常（固定配点）</option>
              <option value="custom">カスタム（問題ごとに設定）</option>
            </Select>
            {eventData.scoringMode === "dynamic" && (
              <div style={{ marginTop: "10px", padding: "10px", background: "#f0f0f0", borderRadius: "4px" }}>
                <small>問題が進むごとに得点倍率が掛かります（デフォルト: 1.2倍）</small>
              </div>
            )}
            {eventData.scoringMode === "normal" && (
              <div style={{ marginTop: "10px", padding: "10px", background: "#f0f0f0", borderRadius: "4px" }}>
                <small>全ての問題で固定の基本スコアが適用されます</small>
              </div>
            )}
            {eventData.scoringMode === "custom" && (
              <div style={{ marginTop: "10px", padding: "10px", background: "#f0f0f0", borderRadius: "4px" }}>
                <small>各問題で個別にスコアを設定できます</small>
              </div>
            )}
          </FormGroup>

          {eventData.scoringMode === "dynamic" && (
            <FormGroup>
              <Label htmlFor="dynamicScoreMultiplier">得点倍率</Label>
              <Input
                id="dynamicScoreMultiplier"
                type="number"
                value={eventData.dynamicScoreMultiplier}
                onChange={(e) => handleEventChange("dynamicScoreMultiplier", e.target.value)}
                min="1.0"
                max="2.0"
                step="0.1"
                disabled={loading}
              />
            </FormGroup>
          )}
        </Section>

        <Section>
          <SectionTitle>問題の設定</SectionTitle>
          
          <FormGroup>
            <CheckboxGroup>
              <Checkbox
                id="useExistingSet"
                type="checkbox"
                checked={eventData.useExistingSet}
                onChange={(e) => handleEventChange("useExistingSet", e.target.checked)}
                disabled={loading}
              />
              <Label htmlFor="useExistingSet">既存のセットを使用する</Label>
            </CheckboxGroup>
          </FormGroup>
          
          {eventData.useExistingSet && (
            <FormGroup>
              <Label htmlFor="selectedSetId">セットを選択 *</Label>
              <Select
                id="selectedSetId"
                value={eventData.selectedSetId}
                onChange={(e) => handleEventChange("selectedSetId", e.target.value)}
                disabled={loading}
                required={eventData.useExistingSet}
              >
                <option value="">セットを選択してください</option>
                {availableSets.map(set => (
                  <option key={set.setId} value={set.setId}>
                    {set.setName} ({set.problemCount}問) - 作成者: {set.creator}
                  </option>
                ))}
              </Select>
            </FormGroup>
          )}
        </Section>

        <Section>
          <SectionTitle>イベント設定</SectionTitle>

          <FormGroup>
            <Label htmlFor="maxParticipants">最大参加人数（空欄で無制限）</Label>
            <Input
              id="maxParticipants"
              type="number"
              value={eventData.maxParticipants}
              onChange={(e) => handleEventChange("maxParticipants", e.target.value)}
              min="1"
              disabled={loading}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="timeLimit">制限時間（秒）（空欄で無制限）</Label>
            <Input
              id="timeLimit"
              type="number"
              value={eventData.timeLimit}
              onChange={(e) => handleEventChange("timeLimit", e.target.value)}
              min="1"
              disabled={loading}
            />
          </FormGroup>

          <FormGroup>
            <CheckboxGroup>
              <Checkbox
                id="allowLateJoin"
                type="checkbox"
                checked={eventData.allowLateJoin}
                onChange={(e) => handleEventChange("allowLateJoin", e.target.checked)}
                disabled={loading}
              />
              <Label htmlFor="allowLateJoin">途中参加を許可する</Label>
            </CheckboxGroup>
          </FormGroup>

          <FormGroup>
            <CheckboxGroup>
              <Checkbox
                id="showRankingDuringEvent"
                type="checkbox"
                checked={eventData.showRankingDuringEvent}
                onChange={(e) => handleEventChange("showRankingDuringEvent", e.target.checked)}
                disabled={loading}
              />
              <Label htmlFor="showRankingDuringEvent">イベント中にランキングを表示する</Label>
            </CheckboxGroup>
          </FormGroup>
        </Section>

        {!eventData.useExistingSet && (
          <Section>
            <SectionTitle>問題一覧</SectionTitle>
            
            <ProblemList>
            {problems.map((problem, index) => (
              <ProblemCard key={problem.id}>
                <ProblemHeader>
                  <ProblemTitle>問題 {index + 1}</ProblemTitle>
                  {problems.length > 1 && (
                    <RemoveButton
                      type="button"
                      onClick={() => removeProblem(problem.id)}
                      disabled={loading}
                    >
                      削除
                    </RemoveButton>
                  )}
                </ProblemHeader>

                <FormGroup>
                  <Label>問題タイトル（任意）</Label>
                  <Input
                    type="text"
                    value={problem.title}
                    onChange={(e) => handleProblemChange(problem.id, "title", e.target.value)}
                    placeholder={`問題 ${index + 1}`}
                    disabled={loading}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>問題開始前画像（任意）</Label>
                  <ImageUploadArea
                    onClick={() => fileInputRefs.current[`before-${problem.id}`]?.click()}
                  >
                    クリックして画像を選択（複数可）
                  </ImageUploadArea>
                  <input
                    ref={el => fileInputRefs.current[`before-${problem.id}`] = el}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={(e) => handleImageSelect(problem.id, "beforeImage", e.target.files)}
                  />
                  {problem.beforeImageUrls.length > 0 && (
                    <ImagePreview>
                      {problem.beforeImageUrls.map((url, i) => (
                        <PreviewImage key={i} src={url} alt={`Before ${i + 1}`} />
                      ))}
                    </ImagePreview>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>問題画像 *</Label>
                  <ImageUploadArea
                    onClick={() => fileInputRefs.current[`problem-${problem.id}`]?.click()}
                  >
                    クリックして画像を選択（複数可）
                  </ImageUploadArea>
                  <input
                    ref={el => fileInputRefs.current[`problem-${problem.id}`] = el}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={(e) => handleImageSelect(problem.id, "image", e.target.files)}
                  />
                  {problem.imageUrls.length > 0 && (
                    <ImagePreview>
                      {problem.imageUrls.map((url, i) => (
                        <PreviewImage key={i} src={url} alt={`Problem ${i + 1}`} />
                      ))}
                    </ImagePreview>
                  )}
                </FormGroup>

                {(problem.imageUrls.length > 1 || problem.beforeImageUrls.length > 0) && (
                  <FormGroup>
                    <Label>画像切り替え設定</Label>
                    <Select
                      value={problem.imageSwitchMode}
                      onChange={(e) => handleProblemChange(problem.id, "imageSwitchMode", e.target.value)}
                      disabled={loading}
                    >
                      <option value="manual">手動切り替え</option>
                      <option value="auto">自動切り替え</option>
                    </Select>
                    {problem.imageSwitchMode === "auto" && (
                      <Input
                        type="number"
                        value={problem.autoSwitchInterval}
                        onChange={(e) => handleProblemChange(problem.id, "autoSwitchInterval", e.target.value)}
                        placeholder="切り替え間隔（秒）"
                        min="1"
                        style={{ marginTop: "10px" }}
                        disabled={loading}
                      />
                    )}
                  </FormGroup>
                )}

                <FormGroup>
                  <Label>解答画像（任意）</Label>
                  <ImageUploadArea
                    onClick={() => fileInputRefs.current[`answer-${problem.id}`]?.click()}
                  >
                    クリックして画像を選択（複数可）
                  </ImageUploadArea>
                  <input
                    ref={el => fileInputRefs.current[`answer-${problem.id}`] = el}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={(e) => handleImageSelect(problem.id, "answerImage", e.target.files)}
                  />
                  {problem.answerImageUrls.length > 0 && (
                    <ImagePreview>
                      {problem.answerImageUrls.map((url, i) => (
                        <PreviewImage key={i} src={url} alt={`Answer ${i + 1}`} />
                      ))}
                    </ImagePreview>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>正解（カンマ区切りで複数可） *</Label>
                  <Input
                    type="text"
                    value={problem.answers}
                    onChange={(e) => handleProblemChange(problem.id, "answers", e.target.value)}
                    placeholder="例: 答え1, 答え2"
                    required
                    disabled={loading}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>入力タイプ</Label>
                  <Select
                    value={problem.inputType}
                    onChange={(e) => handleProblemChange(problem.id, "inputType", e.target.value)}
                    disabled={loading}
                  >
                    <option value="text">テキスト</option>
                    <option value="number">数値</option>
                    <option value="choice">選択式</option>
                    <option value="multiple">複数選択</option>
                  </Select>
                </FormGroup>

                {(problem.inputType === "choice" || problem.inputType === "multiple") && (
                  <FormGroup>
                    <Label>選択肢（カンマ区切り）</Label>
                    <Input
                      type="text"
                      value={problem.choices}
                      onChange={(e) => handleProblemChange(problem.id, "choices", e.target.value)}
                      placeholder="例: 選択肢1, 選択肢2, 選択肢3"
                      disabled={loading}
                    />
                  </FormGroup>
                )}

                {eventData.scoringMode === "custom" && (
                  <FormGroup>
                    <Label>基本スコア</Label>
                    <Input
                      type="number"
                      value={problem.baseScore}
                      onChange={(e) => handleProblemChange(problem.id, "baseScore", e.target.value)}
                      min="0"
                      disabled={loading}
                    />
                  </FormGroup>
                )}

                <FormGroup>
                  <Label>制限時間（秒）</Label>
                  <Input
                    type="number"
                    value={problem.timeLimit}
                    onChange={(e) => handleProblemChange(problem.id, "timeLimit", e.target.value)}
                    min="1"
                    disabled={loading}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>最大解答回数</Label>
                  <Input
                    type="number"
                    value={problem.maxSubmissions}
                    onChange={(e) => handleProblemChange(problem.id, "maxSubmissions", e.target.value)}
                    min="1"
                    disabled={loading}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>ヒント（任意）</Label>
                  <TextArea
                    value={problem.hint}
                    onChange={(e) => handleProblemChange(problem.id, "hint", e.target.value)}
                    disabled={loading}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>解説（任意）</Label>
                  <TextArea
                    value={problem.explanation}
                    onChange={(e) => handleProblemChange(problem.id, "explanation", e.target.value)}
                    disabled={loading}
                  />
                </FormGroup>
              </ProblemCard>
            ))}
            </ProblemList>

            <AddProblemButton
              type="button"
              onClick={addProblem}
              disabled={loading}
              style={{ marginTop: "20px" }}
            >
              問題を追加
            </AddProblemButton>
          </Section>
        )}

        <ButtonGroup>
          <CancelButton
            type="button"
            onClick={() => navigate(`/v3/${projectId}/mypage`)}
            disabled={loading}
          >
            キャンセル
          </CancelButton>
          <SaveButton type="submit" disabled={loading}>
            {loading ? "作成中..." : "イベントを作成"}
          </SaveButton>
        </ButtonGroup>
      </Form>
    </Container>
  );
};

export default V3EventCreate;